// CSS sanitization helpers for chart formatting so that no malicious CSS can be injected from corrupted chart configuration payloads
// It's hard to guarantee that all chart configuration options are completely safe, but at least the configuration bodies passed in as `style` tags for components should be sanitized here

// Import font sizes from the reporting components to ensure consistency
import { fontSizes } from 'components/reporting/sidebar/components';

// Add new properties as needed
const SAFE_CSS_PROPERTIES = new Set([
  'fontSize',
  'fontWeight',
  'fontStyle',
  'color',
]);
const SAFE_CSS_VALUES = {
  fontWeight: new Set(['normal', 'bold']),
  fontStyle: new Set(['normal', 'italic']),
};

// Convert the imported font sizes to the format we need (numbers to px strings)
const VALID_FONT_SIZES = new Set(fontSizes.map(size => `${size}px`));

/**
 * Validates if a CSS value is safe for a given property
 * @param {string} property - CSS property name
 * @param {any} value - CSS value to validate
 * @returns {boolean} - true if safe, false otherwise
 */
function isSafeCSSValue(property, value) {
  if (value === null || value === undefined || value === '') {
    return true; // Allow clearing values
  }

  const stringValue = String(value);

  // Check against allowlisted values for specific properties
  if (SAFE_CSS_VALUES[property]) {
    return SAFE_CSS_VALUES[property].has(stringValue);
  }

  // Validate font sizes (only allow exact values from the UI)
  if (property === 'fontSize') {
    return VALID_FONT_SIZES.has(stringValue);
  }

  // Validate colors (hex, rgb, rgba, named colors)
  if (property === 'color') {
    return isValidColor(stringValue);
  }

  return false;
}

/**
 * Validates if a color value is safe (hex, RGB/RGBA functions, or named colors)
 * @param {string} color - Color value to validate
 */
function isValidColor(color) {
  if (typeof color !== 'string') return false;

  const colorStr = color.trim();
  if (colorStr.length === 0) return false;

  // Allow hex colors (#abc or #abcdef)
  if (/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(colorStr)) {
    return true;
  }

  // Allow RGB/RGBA function calls
  if (
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+)?\s*\)$/i.test(colorStr)
  ) {
    return true;
  }

  // Allow named colors (single word strings without suspicious characters)
  if (/^[a-zA-Z]+$/.test(colorStr)) {
    return true;
  }

  return false;
}

/**
 * Core sanitization logic that validates and sanitizes CSS properties
 * @param {Object} styles - CSS styles object
 * @param {string} context - Context for logging (optional)
 * @returns {Object} - Sanitized CSS styles object
 */
function sanitizeCSSObject(styles, context = '') {
  if (!styles || typeof styles !== 'object') {
    return {};
  }

  const sanitized = {};
  const contextSuffix = context ? ` in ${context}` : '';

  Object.keys(styles).forEach(property => {
    if (SAFE_CSS_PROPERTIES.has(property)) {
      const value = styles[property];
      if (isSafeCSSValue(property, value)) {
        sanitized[property] = value;
      } else {
        console.warn(
          `Unpermitted CSS value "${value}" for property "${property}"${contextSuffix} was removed`
        );
      }
    } else {
      console.warn(
        `Unpermitted CSS property "${property}"${contextSuffix} was removed`
      );
    }
  });

  return sanitized;
}

/**
 * Sanitizes a CSS style object by removing unsafe properties and values
 * @param {Object} styles - CSS styles object
 * @returns {Object} - Sanitized CSS styles object
 */
export function sanitizeStyles(styles) {
  return sanitizeCSSObject(styles);
}

/**
 * Creates a safe styles accessor that only allows explicit requests for specific format types
 * This prevents accidental exposure of unsanitized style properties
 * @param {Object} configuration - Chart configuration object
 * @returns {Object} - Object with explicit getter methods for each format type
 */
export function createSafeStylesAccessor(configuration) {
  const safeConfig = configuration || {};

  /**
   * Safely sanitizes and returns styles for a specific format type
   * @param {string} formatKey - The format configuration key (e.g., 'legendFormat', 'titleFormat')
   * @returns {Object} - Sanitized styles object
   */
  function sanitizeFormatStyles(formatKey) {
    return sanitizeCSSObject(safeConfig[formatKey], formatKey);
  }

  return {
    // Explicit getters for each supported format type
    getLegendFormat: () => sanitizeFormatStyles('legendFormat'),
    getTitleFormat: () => sanitizeFormatStyles('titleFormat'),
    getDataLabelFormat: () => sanitizeFormatStyles('dataLabelFormat'),
    getHorizontalAxisTitleFormat: () =>
      sanitizeFormatStyles('horizontalAxisTitleFormat'),
    getVerticalAxisTitleFormat: () =>
      sanitizeFormatStyles('verticalAxisTitleFormat'),
    getHorizontalAxisLabelFormat: () =>
      sanitizeFormatStyles('horizontalAxisLabelFormat'),
    getVerticalAxisLabelFormat: () =>
      sanitizeFormatStyles('verticalAxisLabelFormat'),
  };
}
